import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Snackbar,
  Alert,
  Box,
  Chip,
  TablePagination,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Edit as EditIcon 
} from '@mui/icons-material';
import axios from 'axios';
import AdminNavbar from '../admin/AdminNavbar';
import baseURL from '../../baseURL';

const GovernmentInterestsListPage = () => {
  const [interests, setInterests] = useState([]);
  const [selectedInterest, setSelectedInterest] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    fetchGovernmentInterests();
  }, []);

  const fetchGovernmentInterests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/government-interests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setInterests(response.data.data);
    } catch (error) {
      setSnackbarMessage('Failed to fetch government interests');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleDeleteInterest = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${baseURL}/government-interests/${selectedInterest._id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSnackbarMessage('Government Interest deleted successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      
      // Refresh interests list
      fetchGovernmentInterests();
      
      // Close delete dialog
      setOpenDeleteDialog(false);
    } catch (error) {
      setSnackbarMessage(error.response?.data?.message || 'Error deleting government interest');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <AdminNavbar />
      <Paper 
        elevation={3} 
        sx={{ 
          width: '100%', 
          mb: 2, 
          borderRadius: 2,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: 600, 
              color: 'primary.main',
              mb: 3
            }}
          >
            Government Interests Management
          </Typography>

          <TableContainer>
            <Table sx={{ minWidth: 750 }}>
              <TableHead sx={{ backgroundColor: 'grey.100' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Interest Rate</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Interest Rate (Words)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Duration (Words)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? interests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : interests
                ).map((interest) => (
                  <TableRow 
                    key={interest._id} 
                    hover 
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      transition: 'background-color 0.3s',
                      '&:hover': { backgroundColor: 'grey.50' }
                    }}
                  >
                    <TableCell>
                      <Chip 
                        label={`${interest.interestRateNumeric}%`} 
                        color="primary" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>{interest.interestRateWords}</TableCell>
                    <TableCell>
                      <Chip 
                        label={interest.durationNumeric} 
                        color="secondary" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>{interest.durationWords}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton color="primary" sx={{ mr: 1 }}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          color="error" 
                          onClick={() => {
                            setSelectedInterest(interest);
                            setOpenDeleteDialog(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={interests.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this government interest with rate {selectedInterest?.interestRateNumeric}%?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDeleteDialog(false)} 
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteInterest} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GovernmentInterestsListPage;